import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";

interface UserSettings {
  notifications: boolean;
  emailUpdates: boolean;
  darkMode: boolean;
  language: string;
  currency: string;
  priceAlerts: boolean;
}

const Settings: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [settings, setSettings] = useState<UserSettings>({
    notifications: true,
    emailUpdates: true,
    darkMode: false,
    language: 'en',
    currency: 'AUD',
    priceAlerts: true
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Load settings from localStorage on component mount
  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      const savedSettings = localStorage.getItem(`settings_${user.sub}`);
      if (savedSettings) {
        try {
          setSettings(JSON.parse(savedSettings));
        } catch (error) {
          console.error('Error loading settings:', error);
        }
      }
    }
  }, [isAuthenticated, user]);

  const handleSettingChange = (key: keyof UserSettings, value: boolean | string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveSettings = async () => {
    if (!isAuthenticated || !user?.sub) return;

    setIsSaving(true);
    setSaveMessage('');

    try {
      // Save to localStorage (in a real app, you'd save to your backend)
      localStorage.setItem(`settings_${user.sub}`, JSON.stringify(settings));
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Error saving settings. Please try again.');
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      notifications: true,
      emailUpdates: true,
      darkMode: false,
      language: 'en',
      currency: 'AUD',
      priceAlerts: true
    });
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-start', 
        alignItems: 'flex-start', 
        height: '400px',
        fontSize: '1.2rem',
        color: '#666',
        padding: '40px 20px'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ 
        textAlign: 'left', 
        padding: '60px 20px',
        color: '#666',
        fontSize: '1.1rem'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔒</div>
        <div>Please log in to access your settings.</div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: window.innerWidth < 480 ? '20px 16px' : '40px 20px', 
      maxWidth: '800px', 
      margin: '0 auto', 
      fontFamily: 'Arial, sans-serif',
      textAlign: 'left'
    }}>
      <h1 style={{ 
        textAlign: 'left', 
        marginBottom: '40px', 
        color: '#333',
        fontSize: window.innerWidth < 480 ? '1.5rem' : window.innerWidth < 768 ? '2rem' : '2.5rem',
        fontWeight: 'bold'
      }}>
        Settings
      </h1>

      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0'
      }}>
        {/* User Info Section */}
        <div style={{ 
          marginBottom: '40px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <h2 style={{ 
            color: '#2c5aa0', 
            marginBottom: '16px', 
            fontSize: '1.3rem',
            fontWeight: '600'
          }}>
            Account Information
          </h2>
          <div style={{ color: '#666', fontSize: '1rem' }}>
            <strong>Email:</strong> {user?.email || 'Not available'}
          </div>
          <div style={{ color: '#666', fontSize: '1rem', marginTop: '8px' }}>
            <strong>Name:</strong> {user?.name || 'Not available'}
          </div>
        </div>

        {/* Notification Settings */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ 
            color: '#2c5aa0', 
            marginBottom: '20px', 
            fontSize: '1.3rem',
            fontWeight: '600'
          }}>
            Notifications
          </h2>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              fontSize: '1rem',
              color: '#333'
            }}>
              <input
                type="checkbox"
                checked={settings.notifications}
                onChange={(e) => handleSettingChange('notifications', e.target.checked)}
                style={{ marginRight: '12px', transform: 'scale(1.2)' }}
              />
              Enable push notifications
            </label>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              fontSize: '1rem',
              color: '#333'
            }}>
              <input
                type="checkbox"
                checked={settings.emailUpdates}
                onChange={(e) => handleSettingChange('emailUpdates', e.target.checked)}
                style={{ marginRight: '12px', transform: 'scale(1.2)' }}
              />
              Email updates and newsletters
            </label>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              fontSize: '1rem',
              color: '#333'
            }}>
              <input
                type="checkbox"
                checked={settings.priceAlerts}
                onChange={(e) => handleSettingChange('priceAlerts', e.target.checked)}
                style={{ marginRight: '12px', transform: 'scale(1.2)' }}
              />
              Price drop alerts
            </label>
          </div>
        </div>

        {/* Appearance Settings */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ 
            color: '#2c5aa0', 
            marginBottom: '20px', 
            fontSize: '1.3rem',
            fontWeight: '600'
          }}>
            Appearance
          </h2>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              fontSize: '1rem',
              color: '#333'
            }}>
              <input
                type="checkbox"
                checked={settings.darkMode}
                onChange={(e) => handleSettingChange('darkMode', e.target.checked)}
                style={{ marginRight: '12px', transform: 'scale(1.2)' }}
              />
              Dark mode (Coming soon)
            </label>
          </div>
        </div>

        {/* Preferences Settings */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ 
            color: '#2c5aa0', 
            marginBottom: '20px', 
            fontSize: '1.3rem',
            fontWeight: '600'
          }}>
            Preferences
          </h2>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontSize: '1rem',
              color: '#333',
              fontWeight: '500'
            }}>
              Language
            </label>
            <select
              value={settings.language}
              onChange={(e) => handleSettingChange('language', e.target.value)}
              style={{
                padding: '8px 12px',
                fontSize: '1rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                minWidth: '150px'
              }}
            >
              <option value="en">English</option>
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontSize: '1rem',
              color: '#333',
              fontWeight: '500'
            }}>
              Currency
            </label>
            <select
              value={settings.currency}
              onChange={(e) => handleSettingChange('currency', e.target.value)}
              style={{
                padding: '8px 12px',
                fontSize: '1rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                minWidth: '150px'
              }}
            >
              <option value="AUD">AUD (A$)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CAD">CAD (C$)</option>
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          alignItems: 'flex-start',
          marginTop: '40px',
          paddingTop: '32px',
          borderTop: '2px solid #e0e0e0'
        }}>
          <button
            onClick={saveSettings}
            disabled={isSaving}
            style={{
              padding: '12px 32px',
              backgroundColor: isSaving ? '#ccc' : '#2c5aa0',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>

          <button
            onClick={resetToDefaults}
            style={{
              padding: '12px 32px',
              backgroundColor: '#6b7280',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            Reset to Defaults
          </button>

          {saveMessage && (
            <div style={{
              color: saveMessage.includes('Error') ? '#dc3545' : '#28a745',
              fontSize: '1rem',
              fontWeight: '500'
            }}>
              {saveMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;